import { ElementType, Track } from '@1debit/analytics';

import { getAnalyticsHandler } from 'utils/analytics/eventRouter';

// Hawker analytics events for the RFA guest flow (/pay/rfa/<handle>), M2G style
// but Event Router only. item_id is the payment public_uuid once it exists; no
// prop set here carries the handle, phone, name, email or memo text (the SDK
// still adds the page path, see PA-4583).

export enum RfaPage {
  PROFILE = 'pay_anyone_rfa_profile_page',
  AMOUNT = 'pay_anyone_rfa_amount_page',
  CONFIRM_PHONE = 'pay_anyone_rfa_confirm_phone_page',
  ENTER_CODE = 'pay_anyone_rfa_enter_code_page',
  PROCESSING = 'pay_anyone_rfa_processing_page',
  RECEIPT = 'pay_anyone_rfa_receipt_page',
  PAYMENT_ERROR = 'pay_anyone_rfa_payment_error_page',
  LINK_UNAVAILABLE = 'pay_anyone_rfa_link_unavailable_page',
}

// item_value of pay_anyone_rfa_cta_tapped, one per tappable element.
export type RfaCta =
  | 'rfa_profile_apple_pay_button'
  | 'rfa_amount_next_button'
  | 'rfa_phone_next_button'
  | 'rfa_code_resend_button'
  | 'rfa_code_call_me_button'
  | 'rfa_code_edit_phone_button'
  | 'rfa_error_retry_button'
  | 'rfa_otp_error_sheet_cta'
  | 'rfa_otp_error_sheet_close'
  | 'rfa_receipt_join_chime_banner'
  | 'rfa_header_join_chime_button'
  | 'rfa_header_login_link'
  | 'rfa_footer_join_chime_link'
  | 'rfa_footer_learn_about_chime_link';

export enum RfaAction {
  PHONE_CHALLENGE_STARTED = 'pay_anyone_rfa_phone_challenge_started',
  PHONE_VERIFIED = 'pay_anyone_rfa_phone_verified',
  CODE_RESENT = 'pay_anyone_rfa_code_resent',
  CODE_SUBMITTED = 'pay_anyone_rfa_code_submitted',
  APPLE_PAY_SHEET_OPENED = 'pay_anyone_rfa_apple_pay_sheet_opened',
  APPLE_PAY_CANCELLED = 'pay_anyone_rfa_apple_pay_cancelled',
  PAYMENT_SUBMITTED = 'pay_anyone_rfa_payment_submitted',
  PAYMENT_SUCCEEDED = 'pay_anyone_rfa_payment_succeeded',
  PAYMENT_UNCONFIRMED = 'pay_anyone_rfa_payment_unconfirmed',
}

export enum RfaError {
  PAYMENT = 'pay_anyone_rfa_payment_error',
  PHONE = 'pay_anyone_rfa_phone_error',
  CODE = 'pay_anyone_rfa_code_error',
  LOOKUP = 'pay_anyone_rfa_lookup_error',
  LINK_UNAVAILABLE = 'pay_anyone_rfa_link_unavailable',
}

// Fixed messages: the stable detail goes in error_code, never server text.
const RFA_ERROR_MESSAGES: Record<RfaError, string> = {
  [RfaError.PAYMENT]: 'RFA payment failed',
  [RfaError.PHONE]: 'RFA phone verification failed',
  [RfaError.CODE]: 'RFA code verification failed',
  [RfaError.LOOKUP]: 'RFA lookup failed',
  [RfaError.LINK_UNAVAILABLE]: 'RFA payment link unavailable',
};

export type RfaField = 'amount' | 'memo' | 'phone';

export const trackRfaPage = (page: RfaPage, publicUuid?: string): void => {
  getAnalyticsHandler().trackPage(
    page,
    publicUuid ? { item_id: publicUuid } : undefined
  );
};

export const trackRfaCtaTapped = (
  cta: RfaCta,
  {
    label,
    elementType = ElementType.ELEMENT_TYPE_BUTTON,
    itemType,
    publicUuid,
  }: {
    label: string;
    elementType?: ElementType;
    // Extra non-PII context, e.g. the OTP error sheet variant.
    itemType?: string;
    publicUuid?: string;
  }
): void => {
  getAnalyticsHandler().trackEvent(Track.WebItemClicked, {
    unique_id: 'pay_anyone_rfa_cta_tapped',
    element_type: elementType,
    label,
    item_value: cta,
    ...(itemType && { item_type: itemType }),
    ...(publicUuid && { item_id: publicUuid }),
  });
};

export const trackRfaScamWarningViewed = (): void => {
  getAnalyticsHandler().trackEvent(Track.WebItemViewed, {
    unique_id: 'pay_anyone_rfa_scam_warning_viewed',
    element_type: ElementType.ELEMENT_TYPE_BANNER,
  });
};

export const trackRfaGooglePayComingSoonViewed = (): void => {
  getAnalyticsHandler().trackEvent(Track.WebItemViewed, {
    unique_id: 'pay_anyone_rfa_google_pay_coming_soon_viewed',
    element_type: ElementType.ELEMENT_TYPE_SECTION,
  });
};

// Only the amount carries its value: memo sends 'filled' and phone nothing.
export const trackRfaFieldFilled = (field: RfaField, amount?: string): void => {
  getAnalyticsHandler().trackEvent(Track.WebFieldFilled, {
    unique_id: 'pay_anyone_rfa_field_filled',
    label: field,
    ...(field === 'amount' && amount && { value: amount }),
    ...(field === 'memo' && { value: 'filled' }),
  });
};

export const trackRfaAction = (
  action: RfaAction,
  publicUuid?: string
): void => {
  getAnalyticsHandler().trackEvent(Track.WebActionTriggered, {
    unique_id: action,
    ...(publicUuid && { item_id: publicUuid }),
  });
};

export const trackRfaError = (
  error: RfaError,
  errorCode: string,
  publicUuid?: string
): void => {
  getAnalyticsHandler().trackEvent(Track.WebErrorOccurred, {
    unique_id: error,
    error_code: errorCode,
    error_message: RFA_ERROR_MESSAGES[error],
    ...(publicUuid && { item_id: publicUuid }),
  });
};
